import {debounce, onBlockLoaded} from "@plaudit/frontend-apis";

import {autoUpdateSelector} from "../../../../../../lib/shared-form-logic";

onBlockLoaded({
	blockOrClassName: "plaudit-query-loop/results-count",
	onAdd(element) {
		const queryForm = element.closest("form");
		// We don't want to use this to manage results-count blocks that are being reloaded via AJAX anyway
		if (!queryForm || element.closest(autoUpdateSelector)) {
			return;
		}

		const debounced = debounce(() => {
			let count = 0;
			for (const result of queryForm?.querySelector(".wp-block-post-template")?.querySelectorAll("li") ?? []) {
				if (result.offsetParent !== null) {
					count++;
				}
			}

			let formatString: string;
			if (count === 0) {
				formatString = element.getAttribute("data-no-results") ?? "No results";
			} else if (count === 1) {
				formatString = element.getAttribute("data-one-result") ?? "%s result";
			} else {
				formatString = element.getAttribute("data-some-results") ?? "%s results";
			}
			const text = formatString.replaceAll("%s", count.toString());
			if (element.innerText !== text) {
				element.innerText = text;
			}
		}, 500);
		// We use a debounced mutation observer instead of the queryLoopFormUpdateComplete event because we need to account for hidden results
		const debouncedObserver = new MutationObserver(() => debounced());
		debouncedObserver.observe(queryForm, {childList: true, subtree: true, attributes: true});

		const observer = new MutationObserver(mutations => {
			for (const mutation of mutations) {
				for (const removedNode of mutation.removedNodes) {
					if (removedNode === element) {
						observer.disconnect();
						debouncedObserver.disconnect();
					}
				}
			}
		});
		observer.observe(element.parentElement!, {childList: true});
	}
});

function replaceLiveRegionContents(liveRegion: HTMLElement, resultsUpdatedMessage: string, resultsCountText: string|undefined) {
	liveRegion.replaceChildren(); // This removes all child elements
	const parts: HTMLElement[] = [];

	const resultsUpdatedMessageWrapper = document.createElement("p");
	resultsUpdatedMessageWrapper.innerText = resultsUpdatedMessage;
	parts.push(resultsUpdatedMessageWrapper);

	if (resultsCountText) {
		const resultsCountWrapper = document.createElement("p");
		resultsCountWrapper.innerText = resultsCountText.trim();
		parts.push(resultsCountWrapper);
	}

	liveRegion.append(...parts);
}

document.addEventListener("queryLoopFormInitialized", ({detail: queryLoopForm}) => {
	const rootElement = queryLoopForm.form;
	const liveRegion = document.createElement("div");
	liveRegion.className = "sr-only screen-reader-text";
	liveRegion.ariaLive = "assertive";
	liveRegion.ariaAtomic = "true";
	liveRegion.innerText = "";
	rootElement.prepend(liveRegion);

	const resultsUpdatedMessage = rootElement.getAttribute("data-results-updated-message") ?? "Results Updated";
	const resultsCountElement = rootElement.querySelector<HTMLElement>(".wp-block-plaudit-query-loop-results-count");
	if (resultsCountElement === null || resultsCountElement.closest(autoUpdateSelector)) {
		// This handles AJAX-loaded result count elements
		rootElement.addEventListener("queryLoopFormUpdateComplete", debounce(() => {
			const resultsCountText = rootElement.querySelector<HTMLElement>(".wp-block-plaudit-query-loop-results-count")?.innerText;
			replaceLiveRegionContents(liveRegion, resultsUpdatedMessage, resultsCountText);
		}, 500));
	} else {
		// We don't debounce this one because updates to non-ajax-loaded result count elements are already debounced
		new MutationObserver(() => replaceLiveRegionContents(liveRegion, resultsUpdatedMessage, resultsCountElement.innerText))
			.observe(resultsCountElement, {characterData: true, childList: true});
	}
});
